import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { assetService } from '../assetService';
import {
  AssetFilters,
  AssetInvoiceFilters,
  CategoryScope,
  CreateAssetDto,
  CreateAssetCategoryDto,
  CreateAssetVendorDto,
  CreateAssetInvoiceDto,
} from '../../../types/asset';

// ── Register ────────────────────────────────────────────────────────────

export function useAssets(filters?: AssetFilters) {
  return useQuery({
    queryKey: ['assets', filters],
    queryFn: () => assetService.getAssets(filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useAssetSummary(filters?: AssetFilters) {
  return useQuery({
    queryKey: ['assets', 'summary', filters],
    queryFn: () => assetService.getSummary(filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useCreateAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAssetDto) => assetService.createAsset(data),
    meta: { successMessage: 'Fixed asset recorded' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
    },
  });
}

export function useUpdateAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateAssetDto> }) =>
      assetService.updateAsset(id, data),
    meta: { successMessage: 'Fixed asset updated' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
    },
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => assetService.deleteAsset(id),
    meta: { successMessage: 'Fixed asset deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
    },
  });
}

// ── Categories ──────────────────────────────────────────────────────────

export function useAssetCategories(search?: string, activeOnly = false, scope?: CategoryScope) {
  return useQuery({
    queryKey: ['asset-categories', search, activeOnly, scope],
    queryFn: () => assetService.getCategories(search, activeOnly, scope),
    staleTime: 30000,
  });
}

/** Paginated + searchable categories (used by the categories list page). */
export function usePaginatedAssetCategories(
  page = 1,
  limit = 20,
  search?: string,
  scope?: CategoryScope,
) {
  return useQuery({
    queryKey: ['asset-categories', 'paginated', page, limit, search, scope],
    queryFn: () => assetService.getPaginatedCategories(page, limit, search, scope),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useNextAssetCategoryCode(enabled = true) {
  return useQuery({
    queryKey: ['asset-categories', 'next-code'],
    queryFn: () => assetService.getNextCategoryCode(),
    enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCreateAssetCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAssetCategoryDto) => assetService.createCategory(data),
    meta: { successMessage: 'Category created' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-categories'] });
    },
  });
}

export function useUpdateAssetCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateAssetCategoryDto> }) =>
      assetService.updateCategory(id, data),
    meta: { successMessage: 'Category updated' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-categories'] });
    },
  });
}

export function useDeleteAssetCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => assetService.deleteCategory(id),
    meta: { successMessage: 'Category deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-categories'] });
    },
  });
}

/** Bulk-delete categories as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteAssetCategories() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await assetService.deleteCategory(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-categories'] });
    },
  });
}

// ── Vendors ─────────────────────────────────────────────────────────────

export function useAssetVendors(search?: string, activeOnly = false) {
  return useQuery({
    queryKey: ['asset-vendors', search, activeOnly],
    queryFn: () => assetService.getVendors(search, activeOnly),
    staleTime: 30000,
  });
}

/** Paginated + searchable vendors (used by the vendors list page). */
export function usePaginatedAssetVendors(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['asset-vendors', 'paginated', page, limit, search],
    queryFn: () => assetService.getPaginatedVendors(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useAssetVendor(id?: string) {
  return useQuery({
    queryKey: ['asset-vendors', 'detail', id],
    queryFn: () => assetService.getVendor(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useCreateAssetVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAssetVendorDto) => assetService.createVendor(data),
    meta: { successMessage: 'Vendor created' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-vendors'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useUpdateAssetVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateAssetVendorDto> }) =>
      assetService.updateVendor(id, data),
    meta: { successMessage: 'Vendor updated' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-vendors'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useDeleteAssetVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => assetService.deleteVendor(id),
    meta: { successMessage: 'Vendor deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-vendors'] });
    },
  });
}

/** Bulk-delete vendors as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteAssetVendors() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await assetService.deleteVendor(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-vendors'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

/**
 * Bulk-create vendors as a single silent mutation (loops the create endpoint).
 * Reports per-row failures so the CSV importer can surface them.
 */
export function useBulkCreateAssetVendors() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (rows: { rowNumber: number; data: CreateAssetVendorDto }[]) => {
      const failed: { rowNumber: number; message: string }[] = [];
      let ok = 0;
      for (const row of rows) {
        try {
          await assetService.createVendor(row.data);
          ok += 1;
        } catch (err: any) {
          failed.push({
            rowNumber: row.rowNumber,
            message: err?.response?.data?.message || 'Failed to create vendor',
          });
        }
      }
      return { ok, failed };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-vendors'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

// ── Purchase invoices (multi-asset) ─────────────────────────────────────

export function useAssetInvoices(filters?: AssetInvoiceFilters) {
  return useQuery({
    queryKey: ['asset-invoices', filters],
    queryFn: () => assetService.getInvoices(filters),
    staleTime: 30000,
  });
}

export function useAssetInvoice(id?: string) {
  return useQuery({
    queryKey: ['asset-invoices', 'detail', id],
    queryFn: () => assetService.getInvoice(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useCreateAssetInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAssetInvoiceDto) => assetService.createInvoice(data),
    meta: { successMessage: 'Asset invoice recorded' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
    },
  });
}

export function useDeleteAssetInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => assetService.deleteInvoice(id),
    meta: { successMessage: 'Asset invoice deleted' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['cashbook'] });
    },
  });
}
